/**
 * CREATE RULE MODAL COMPONENT
 * Modal dialog for creating new rules in the system
 */

// React imports
import React from 'react';

// Material-UI imports
import { 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle, 
  TextField 
} from '@mui/material';

// API imports
import { createRule } from '../../api/rule';

// Context imports
import { RuleStore, useRuleContext } from '../../context/RuleStore';
import { UserStore, useUserContext } from '../../context/UserStore';

/**
 * CreateRuleModal Component
 * Renders a modal dialog with form fields for creating a new rule
 */
export const CreateRuleModal = ({ onRuleCreated }) => {
  // Context hooks for state management
  const { state: userState, dispatch: userDispatch, action: userActions } = useUserContext();
  const { state: ruleState, dispatch: ruleSipatch, action: ruleActions } = useRuleContext();

  /**
   * Handle form submission for creating a new rule
   * @param {Event} event - Form submission event
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const formJson = Object.fromEntries(formData.entries());
    const { name, points, margin,description } = formJson;
    
    // Validation checks
    if (name.length > 30) {
      alert('Rule name must be less than 30 characters');
      return;
    }
    
    const pointsValue = parseInt(points);
    if (Math.abs(pointsValue) >= 1000) {
      alert('Points must be less than 1000 (positive or negative)');
      return;
    }
    
        const marginValue = parseInt(margin);
    if (Math.abs(marginValue) >= 1000) {
      alert('Points must be less than 1000 (positive or negative)');
      return;
    }
    
    // Create new rule via API
    await createRule({
      name,
      points,
      margin,
      description,
    });
    
    // Refresh rules list and close modal
    ruleActions.getRulesAsyncAction();
    ruleActions.hideEditRuleModalAction();
    
    // Show notification if callback provided
    if (onRuleCreated) {
      onRuleCreated();
    }
  };

  return (
    <Dialog
      open={!!ruleState?.isEditRuleModalOpen}
      onClose={() => {
        ruleActions.hideEditRuleModalAction();
      }}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
      }}
    >
      {/* Modal Header */}
      <DialogTitle>Add New Rule</DialogTitle>
      
      {/* Modal Content */}
      <DialogContent>
        <DialogContentText>
          Create a new rule by filling out the information below.
        </DialogContentText>
        
        {/* Rule Name Field */}
        <TextField
          autoFocus
          required
          margin="dense"
          id="name"
          name="name"
          label="Name"
          type="text"
          fullWidth
          variant="standard"
          placeholder="Enter rule name"
          inputProps={{ maxLength: 30 }}
          helperText="Maximum 30 characters"
        />
        
        {/* Points Field */}
        <TextField
          required
          margin="dense"
          id="points"
          name="points"
          label="Points"
          type="number"
          fullWidth
          variant="standard"
          placeholder="Enter point value"
          inputProps={{ min: -999, max: 999 }}
          helperText="Must be between -999 and 999"
        />
                {/* Points Field */}
        <TextField
          required
          margin="dense"
          id="margin"
          name="margin"
          label="Margin"
          type="number"
          fullWidth
          variant="standard"
          placeholder="Enter margin value"
          inputProps={{ min: -999, max: 999 }}
          helperText="Must be between -999 and 999"
        />

        {/* Description Field */}
        <TextField
          margin="dense"
          id="description"
          name="description"
          label="Description"
          type="text"
          fullWidth
          variant="standard"
          placeholder="Enter rule description (optional)"
        />
      </DialogContent>
      
      {/* Modal Actions */}
      <DialogActions>
        <Button
          onClick={() => {
            ruleActions.hideEditRuleModalAction();
          }}
        >
          Cancel
        </Button>
        <Button type="submit" variant="contained">
          Create Rule
        </Button>
      </DialogActions>
    </Dialog>
  );
};
